import {
  HomePageProxy,
  IrrigationOptimizationChartWithPlantationViewModel,
  IrrigationRecommendationViewModel,
  IrrigationRecommendationViewModelListResponseResult,
  UserEnterpriseInfoViewModel,
} from '@/app/proxy/proxies';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DkSelectPlantationComponent } from '../dk-select-plantation/dk-select-plantation.component';
import { SharedModule } from '@/app/shared/shared.module';
import { finalize } from 'rxjs';
import { WidgetInfoBoxComponent } from '../home-warnings-and-alerts/widget-info-box/widget-info-box.component';
import { Router } from '@angular/router';
import { MixpanelTrackerService } from '@/app/core/analytics/mixpanel-tracker.service';

@Component({
  selector: 'app-home-upcoming-irrigations',
  imports: [SharedModule, DkSelectPlantationComponent, WidgetInfoBoxComponent],
  templateUrl: './home-upcoming-irrigations.component.html',
  styleUrl: './home-upcoming-irrigations.component.less',
})
export class HomeUpcomingIrrigationsComponent implements OnChanges {
  @Input() selectedEnterprise: UserEnterpriseInfoViewModel;
  @Input() hasIrrigationProgramAccess = false;

  @Output() readonly getPremiumEvent = new EventEmitter<void>();

  upcomingIrrigationLoading = true;
  selectablePlantationsForUpcomingIrrigation: IrrigationRecommendationViewModel[] = [];
  selectedPlantationForUpcomingIrrigation: IrrigationRecommendationViewModel | null = null;
  selectedPlantationRecommendations: IrrigationRecommendationViewModel[] = [];

  fetchingError = false;

  private recommendationsByPlantationId = new Map<string, IrrigationRecommendationViewModel[]>();

  constructor(private _homeProxy: HomePageProxy, private _router: Router, private mixpanelTracker: MixpanelTrackerService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['hasIrrigationProgramAccess'] || changes['selectedEnterprise']) {
      this.resetUpcomingIrrigationState();
      if (this.selectedEnterprise && this.hasIrrigationProgramAccess) {
        this.getUpcomingIrrigationPlantations();
      } else {
        this.upcomingIrrigationLoading = false;
      }
    }
  }

  getUpcomingIrrigationPlantations() {
    this.fetchingError = false;
    this.upcomingIrrigationLoading = true;
    this._homeProxy
      .homePage_GetIrrigationProgramByEnterprise(this.selectedEnterprise?.enterpriseId)
      .pipe(
        finalize(() => {
          this.upcomingIrrigationLoading = false;
        })
      )
      .subscribe((res: IrrigationRecommendationViewModelListResponseResult) => {
        if (res.isSuccess) {
          this.applyIrrigationRecommendations(res.result);
        } else {
          this.fetchingError = true;
        }
      });
  }

  onSelectPlantationForUpcomingIrrigation(
    plantation: IrrigationOptimizationChartWithPlantationViewModel | IrrigationRecommendationViewModel
  ) {
    this.setSelectedPlantation(plantation as IrrigationRecommendationViewModel);
    const typed = this.selectedPlantationForUpcomingIrrigation;
    if (typed?.plantationId) {
      this.mixpanelTracker.trackHomeUpcomingIrrigationPlantationChanged({
        'plantation id': typed.plantationId,
        'plantation name': typed.plantationName ?? '',
      });
    }
  }

  private resetUpcomingIrrigationState(): void {
    this.recommendationsByPlantationId = new Map();
    this.selectablePlantationsForUpcomingIrrigation = [];
    this.selectedPlantationForUpcomingIrrigation = null;
    this.selectedPlantationRecommendations = [];
  }

  private applyIrrigationRecommendations(
    recommendations: IrrigationRecommendationViewModel[] | undefined | null
  ): void {
    const grouped = new Map<string, IrrigationRecommendationViewModel[]>();

    for (const recommendation of recommendations ?? []) {
      if (!recommendation?.plantationId) {
        continue;
      }

      const plantationRecommendations = grouped.get(recommendation.plantationId) ?? [];
      plantationRecommendations.push(recommendation);
      grouped.set(recommendation.plantationId, plantationRecommendations);
    }

    grouped.forEach((plantationRecommendations) => {
      plantationRecommendations.sort(
        (left, right) => this.getRecommendationTime(left) - this.getRecommendationTime(right)
      );
    });

    this.recommendationsByPlantationId = grouped;
    this.selectablePlantationsForUpcomingIrrigation = Array.from(grouped.values()).map(
      (plantationRecommendations) => plantationRecommendations[0]
    );

    const previouslySelectedId = this.selectedPlantationForUpcomingIrrigation?.plantationId;
    const matchingPlantation = this.selectablePlantationsForUpcomingIrrigation.find(
      (plantation) => plantation.plantationId === previouslySelectedId
    );

    this.setSelectedPlantation(matchingPlantation ?? this.selectablePlantationsForUpcomingIrrigation[0] ?? null);
  }

  private setSelectedPlantation(plantation: IrrigationRecommendationViewModel | null): void {
    this.selectedPlantationForUpcomingIrrigation = plantation;
    this.selectedPlantationRecommendations = plantation?.plantationId
      ? (this.recommendationsByPlantationId.get(plantation.plantationId) ?? [])
      : [];
  }

  private getRecommendationTime(recommendation: IrrigationRecommendationViewModel): number {
    return recommendation.recommendedDate?.toMillis?.() ?? Number.MAX_SAFE_INTEGER;
  }

  goToIrrigation() {
    this._router.navigate(['/services/irrigation']);
  }
}
